import { memo, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Crown, Star, Shield, CheckCircle, XCircle, Edit3 } from "lucide-react";
import CustomVideoPlayer from "../CustomVideoPlayer"; // تأكد من مطابقة مسار ملف المشغل الجديد

const ROLE_META = {
  admin:  { Icon: Shield, color: "text-gold-500",  label: "منظم" },
  leader: { Icon: Crown,  color: "text-gold-400",  label: "قائد" },
  vice:   { Icon: Star,   color: "text-white/70",  label: "نائب" },
};

function ImageMediaBlock({ m, showOpponentButtons, onOpponentDecide }) {
  return (
    <div>
      <img src={m.image} alt="screenshot" className="rounded-md mb-1.5 max-w-full max-h-44 object-contain" />
      {m.opponent_decision && (
        <div className={`text-[10px] mb-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
          m.opponent_decision === "accept" ? "bg-emerald-500/10 text-emerald-400" : "bg-destructive/10 text-destructive"
        }`}>
          {m.opponent_decision === "accept" ? <CheckCircle size={10} /> : <XCircle size={10} />}
          <span>{m.opponent_decision === "accept" ? "تأكيد الخصم" : "رفض الخصم — يتدخل المنظم"}</span>
        </div>
      )}
      {showOpponentButtons && (
        <div className="flex gap-1 mt-1" data-testid={`img-buttons-${m.id}`}>
          <button data-testid={`img-accept-${m.id}`} onClick={() => onOpponentDecide(m.id, "accept")} className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 flex items-center gap-1 text-[10px]">
            <CheckCircle size={10} /> اقبل
          </button>
          <button data-testid={`img-reject-${m.id}`} onClick={() => onOpponentDecide(m.id, "reject")} className="px-2 py-0.5 rounded bg-destructive/10 text-destructive hover:bg-destructive/20 flex items-center gap-1 text-[10px]">
            <XCircle size={10} /> ارفض
          </button>
        </div>
      )}
    </div>
  );
}

function VideoMediaBlock({ m, isAdmin, onAdminDecide }) {
  const isReject = m.admin_decision === "reject";
  const isApprove = m.admin_decision === "approve";
  return (
    <div>
      {/* تصغير عرض مشغل الفيديو ليصبح مدمجاً وأنيقاً داخل الشات */}
      <div className="w-[320px] max-w-full mb-1.5">
        <CustomVideoPlayer src={m.video} />
      </div>

      {m.admin_decision && (
        <div
          data-testid={`video-decision-${m.id}`}
          className={`text-[10px] mb-1 flex items-start gap-1 px-2 py-1 rounded ${
            isApprove ? "bg-emerald-500/10 text-emerald-400" : "bg-destructive/10 text-destructive"
          }`}
        >
          {isApprove ? <CheckCircle size={11} className="mt-0.5 shrink-0" /> : <XCircle size={11} className="mt-0.5 shrink-0" />}
          <div className="flex-1">
            <div className="font-bold">قرار المنظم: {isApprove ? "موافقة" : "رفض"}</div>
            {isReject && m.admin_note && (
              <div className="text-white/70 mt-0.5 whitespace-pre-wrap">{m.admin_note}</div>
            )}
          </div>
        </div>
      )}
      {!isReject && m.admin_note && (
        <div className="text-[10px] text-white/60 bg-white/5 rounded p-1 mb-1 flex items-start gap-1">
          <Edit3 size={10} className="mt-0.5 text-gold-500" />
          <span>{m.admin_note}</span>
        </div>
      )}
      {isAdmin && (
        <div className="flex gap-1 mt-1" data-testid={`video-buttons-${m.id}`}>
          <button data-testid={`vid-approve-${m.id}`} onClick={() => onAdminDecide(m.id, "approve")} className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 flex items-center gap-1 text-[10px]"><CheckCircle size={10} /></button>
          <button data-testid={`vid-reject-${m.id}`} onClick={() => onAdminDecide(m.id, "reject")} className="px-2 py-0.5 rounded bg-destructive/10 text-destructive hover:bg-destructive/20 flex items-center gap-1 text-[10px]"><XCircle size={10} /></button>
          <button data-testid={`vid-note-${m.id}`} onClick={() => onAdminDecide(m.id, "note")} className="px-2 py-0.5 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1 text-[10px]"><Edit3 size={10} /></button>
        </div>
      )}
    </div>
  );
}

export default function ChatMessage({ m, user, isAdmin, userClanId, onOpponentDecide, onAdminDecide, matchClans }) {
  const isMine = m.user_id === user?.id;
  const meta = ROLE_META[m.user_role] || ROLE_META.vice;

  const showOpponentButtons =
    m.type === "image" && !m.opponent_decision &&
    userClanId && m.user_clan_id && userClanId !== m.user_clan_id &&
    [matchClans.a, matchClans.b].includes(userClanId);

  return (
    <div className={`flex ${isMine ? "justify-start" : "justify-end"}`}>
      {/* تصغير العرض الأقصى للحاوية إلى 360 بكسل وتصغير البيدنج لتصبح الرسالة مدمجة ونحيفة */}
      <div className={`w-full max-w-[360px] rounded-xl px-2.5 py-2 border ${isMine ? "bg-gold-500/10 border-gold-500/30" : "bg-black/50 border-white/10"}`}>
        <div className={`flex items-center gap-1 text-[10px] mb-0.5 ${meta.color}`}>
          <meta.Icon size={10} />
          <span className="font-bold">{m.username}</span>
          <span className="text-white/30">• {meta.label}</span>
        </div>
        {m.image && (
          <ImageMediaBlock m={m} showOpponentButtons={showOpponentButtons} onOpponentDecide={onOpponentDecide} />
        )}
        {m.video && (
          <VideoMediaBlock m={m} isAdmin={isAdmin&&m.kind!=="zoom_event"} onAdminDecide={onAdminDecide} />
        )}
        {m.kind === "zoom_event" && m.zoom_id && <a className="block underline text-amber-300 text-sm py-2" href={isAdmin?`/matches/${m.match_id}/zoom#zoom-${m.zoom_id}`:`/matches/${m.match_id}#zoom-${m.zoom_id}`}>عرض تفاصيل الزوم</a>}
        {m.text && <div className="text-[11px] whitespace-pre-wrap break-words leading-relaxed">{m.text}</div>}
        <div className="mt-1 flex items-center gap-2 text-[9px]">
          <span className="text-white/30">{new Date(m.created_at).toLocaleTimeString("ar")}</span>
          {m._optimistic && !m._sendError && (
            <span className="text-gold-400/90">جاري الإرسال...</span>
          )}
          {m._sendError && (
            <span className="text-destructive">فشل الإرسال</span>
          )}
        </div>
      </div>
    </div>
  );
}
